import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const ColorContext = createContext();
export const useColors = () => useContext(ColorContext);

export const ColorProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(false);

  const mountedRef = useRef(true);

  const COLORS_ENDPOINT = "/colors";
  const baseURL = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;

  /* -------------------------------------------------- */
  /* Mount safety */
  /* -------------------------------------------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetColors = (updater) => {
    if (!mountedRef.current) return;
    setColors(updater);
  };

  /* -------------------------------------------------- */
  /* FETCH ALL COLORS */
  /* -------------------------------------------------- */
  const fetchColors = useCallback(async () => {
    setLoading(true);
    try {
      if (!token) throw new Error("Missing auth token");

      const res = await fetch(`${baseURL}${COLORS_ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || "Fetch failed");

      safeSetColors(payload?.data || []);
      return payload;
    } catch (err) {
      console.error("[Colors] fetch error:", err);
      if (err.message?.includes("Token")) logout();
      safeSetColors([]);
      throw err;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL, logout]);

  /* -------------------------------------------------- */
  /* CREATE COLOR */
  /* -------------------------------------------------- */
  const addColor = useCallback(
    async (data) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${COLORS_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Create failed");

        await fetchColors();
        return payload;
      } catch (err) {
        console.error("[Colors] add error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId, fetchColors]
  );

  /* -------------------------------------------------- */
  /* UPDATE COLOR */
  /* -------------------------------------------------- */
  const updateColor = useCallback(
    async (id, data) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${COLORS_ENDPOINT}/${id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...data,
            update_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchColors();
        return payload;
      } catch (err) {
        console.error("[Colors] update error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId, fetchColors]
  );

  /* -------------------------------------------------- */
  /* DELETE COLOR (SOFT DELETE) */
  /* -------------------------------------------------- */
  const deleteColor = useCallback(
    async (id) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${COLORS_ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            delete_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetColors((prev) => prev.filter((c) => String(c.id) !== String(id)));
        return payload;
      } catch (err) {
        console.error("[Colors] delete error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* -------------------------------------------------- */
  /* TOGGLE STATUS */
  /* -------------------------------------------------- */
  const toggleStatus = useCallback(
    async (id, currentStatus) => {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

      try {
        if (!token) throw new Error("Missing auth token");

        // Optimistic UI update
        safeSetColors((prev) =>
          prev.map((c) =>
            String(c.id) === String(id) ? { ...c, status: newStatus } : c
          )
        );

        const res = await fetch(
          `${baseURL}${COLORS_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              status: newStatus,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.message || "Status update failed");

        return payload;
      } catch (err) {
        console.error("[Colors] toggleStatus error:", err);

        // Rollback UI
        safeSetColors((prev) =>
          prev.map((c) =>
            String(c.id) === String(id) ? { ...c, status: currentStatus } : c
          )
        );
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* -------------------------------------------------- */
  /* INITIAL LOAD */
  /* -------------------------------------------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchColors().catch(() => {});
  }, [isReady, token, fetchColors]);

  return (
    <ColorContext.Provider
      value={{
        colors,
        loading,
        fetchColors,
        addColor,
        updateColor,
        deleteColor,
        toggleStatus,
      }}
    >
      {children}
    </ColorContext.Provider>
  );
};
